import { createElement } from "react"

import { badgeIcon } from "@/lib/badge-icons"
import { LOCKED_SHADOW, TIER_CLASSES, type Tier } from "@/lib/badge-tiers"
import { cn } from "@/lib/utils"

/**
 * OKS-401 — Premium tier medallion.
 *
 * A purely presentational artifact that renders the layered tier
 * treatment (gradient body → inset depth + outer glow → top highlight
 * arc → centered icon glyph) for one badge, given its derived `tier`
 * (see `lib/badge-tiers.ts`), `icon`, `state`, `size`, and `isNew`.
 *
 * It slots inside `<BadgeTile>` (replacing the old flat icon circle), so
 * both the W15 creator grid (60×60 `hero`) and the W13 profile preview
 * row (48×48 `preview`) inherit the treatment from one integration point.
 *
 * # Accessibility (UX Spec §6)
 *
 * Every decorative layer (gradient body, sheen overlay, icon glyph) is
 * `aria-hidden` — the labelled `<BadgeTile>` wrapper is the only node in
 * the a11y tree, and it announces the tier by name. The NEW chip carries
 * real text (`newLabel`, e.g. "New" / "Nouveau") — never colour-only.
 *
 * The component takes the already-translated `newLabel` as a prop rather
 * than calling `t()` so it stays hermetic and i18n-decoupled.
 */
interface PremiumBadgeProps {
  tier: Tier
  /** Lucide identifier (resolved via `badgeIcon`, with Award fallback). */
  icon: string
  state: "earned" | "locked"
  /**
   * `hero` = 60×60 (grid); `preview` = 48×48 (profile preview row);
   * `detail` = 124×124 (OKS-527 §14 detail-sheet medallion);
   * `corner` = 28×28 (OKS-969 medallion strip at roster density).
   */
  size: "hero" | "preview" | "detail" | "corner"
  /** Pins the celebratory NEW chip when the badge is earned & recent. */
  isNew?: boolean
  /** Already-translated chip label (defaults to "New"). */
  newLabel?: string
  className?: string
}

const SIZE_CLASSES: Record<
  PremiumBadgeProps["size"],
  { medallion: string; icon: string }
> = {
  hero: { medallion: "h-[60px] w-[60px]", icon: "h-7 w-7" },
  preview: { medallion: "h-12 w-12", icon: "h-6 w-6" },
  detail: { medallion: "h-[124px] w-[124px]", icon: "h-14 w-14" },
  // OKS-969 — matches `<BadgeImage>`'s existing `corner` (h-7 w-7) so a
  // milestone and a custom badge render at the SAME size in one strip.
  // Before this rung a milestone could not render at 28px at all, which
  // would have forced OKS-970's 64px roster cell to fork the component
  // rather than pass a prop. Purely additive: the union widens and no
  // existing call site changes.
  corner: { medallion: "h-7 w-7", icon: "h-3.5 w-3.5" },
}

export function PremiumBadge({
  tier,
  icon,
  state,
  size,
  isNew = false,
  newLabel = "New",
  className,
}: PremiumBadgeProps) {
  const earned = state === "earned"
  const tierClasses = TIER_CLASSES[tier] ?? TIER_CLASSES.bronze
  const dims = SIZE_CLASSES[size]
  const iconGlyph = badgeIcon(icon)

  return (
    <div
      data-testid="premium-badge"
      data-tier={tier}
      data-state={state}
      data-size={size}
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full",
        dims.medallion,
        tierClasses.body,
        earned ? tierClasses.glow : LOCKED_SHADOW,
        !earned && "opacity-70 grayscale",
        className,
      )}
    >
      {/* Top highlight arc (sheen) — decorative. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.55)_0%,rgba(255,255,255,0)_55%)]"
      />

      {createElement(iconGlyph, {
        className: cn("relative", dims.icon, tierClasses.icon),
        "aria-hidden": "true",
      })}

      {earned && isNew && (
        <span
          data-testid="premium-badge-new-chip"
          className="absolute -top-1 -left-1 rounded-full bg-[linear-gradient(135deg,#fbbf24_0%,#f59e0b_100%)] px-1.5 py-0.5 text-[9px] leading-none font-bold text-amber-950 shadow-[0_0_8px_rgba(251,191,36,0.85)]"
        >
          {newLabel}
        </span>
      )}
    </div>
  )
}
